import React, { useState, useEffect } from 'react';
import { FaSearch } from 'react-icons/fa';
import { exportToExcel } from '../utils/exportToExcel';
import { bookingsAPI, hotelsAPI } from '../services/api';
import './Bookings.css';

const Bookings = () => {
  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // State for additional filters
  const [hotelFilter, setHotelFilter] = useState('');
  const [checkInDateFilter, setCheckInDateFilter] = useState('');
  const [checkOutDateFilter, setCheckOutDateFilter] = useState('');
  
  // State for hotels to populate the dropdown
  const [hotels, setHotels] = useState([]);

  // State for booking details modal
  const [showBookingDetails, setShowBookingDetails] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);

  // State for sorting
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Fetch bookings and hotels from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch bookings
        const bookingsResponse = await bookingsAPI.getAll();
        setBookings(bookingsResponse.data?.data.bookings || bookingsResponse.data || []);
        
        // Fetch hotels for the filter dropdown
        const hotelsResponse = await hotelsAPI.getAll();
        setHotels(hotelsResponse.data?.data?.hotels || hotelsResponse.data || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, []);

  const filteredBookings = bookings.filter(booking => {
    // Debug: Log the first booking to understand its structure
    if (bookings.length > 0 && !window.bookingLogged) {
      console.log('Sample booking object:', bookings[0]);
      window.bookingLogged = true; // Prevent logging multiple times
    }
    const matchesFilter = filter === 'all' || booking.status === filter;
    const matchesSearch = (booking.id == (searchTerm)) ||
      (booking.customer_name && booking.customer_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (booking.hotel_name && booking.hotel_name.toLowerCase().includes(searchTerm.toLowerCase()));
    
    // Additional filters
    // eslint-disable-next-line
    const matchesHotel = !hotelFilter || 
      booking.hotel_name === hotelFilter;
    
    // Check date filters
    let matchesCheckIn = true;
    let matchesCheckOut = true;
    
    if (checkInDateFilter) {
      const bookingCheckIn = new Date(booking.check_in_date);
      const filterCheckIn = new Date(checkInDateFilter);
      matchesCheckIn = bookingCheckIn >= filterCheckIn;
    }
    
    if (checkOutDateFilter) {
      const bookingCheckOut = new Date(booking.check_out_date);
      const filterCheckOut = new Date(checkOutDateFilter);
      matchesCheckOut = bookingCheckOut <= filterCheckOut;
    }
    
    return matchesFilter && matchesSearch && matchesHotel && matchesCheckIn && matchesCheckOut;
  });

  // Sort the filtered bookings
  const sortedBookings = [...filteredBookings].sort((a, b) => {
    if (!sortConfig.key) return 0;
    
    let aValue = a[sortConfig.key];
    let bValue = b[sortConfig.key];
    
    // Special handling for total_price to ensure numeric sorting
    if (sortConfig.key === 'total_price') {
      aValue = parseFloat(a.total_price) || 0;
      bValue = parseFloat(b.total_price) || 0;
    }
    
    // Special handling for number_of_guests to ensure numeric sorting
    if (sortConfig.key === 'number_of_guests') {
      aValue = parseInt(a.number_of_guests) || 0;
      bValue = parseInt(b.number_of_guests) || 0;
    }
    
    // Handle different data types for sorting
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      const comparison = aValue.toLowerCase().localeCompare(bValue.toLowerCase());
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    } else if (typeof aValue === 'number' && typeof bValue === 'number') {
      const comparison = aValue - bValue;
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    } else if (aValue instanceof Date && bValue instanceof Date) {
      const comparison = aValue - bValue;
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    } else {
      // Handle date strings
      const aDate = new Date(aValue);
      const bDate = new Date(bValue);
      if (!isNaN(aDate) && !isNaN(bDate)) {
        const comparison = aDate - bDate;
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      }
      // Handle mixed types or other cases
      const comparison = String(aValue).toLowerCase().localeCompare(String(bValue).toLowerCase());
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    }
  });

  const getStatusClass = (status) => {
    switch (status) {
      case 'confirmed': return 'status-confirmed';
      case 'pending': return 'status-pending';
      case 'cancelled': return 'status-cancelled';
      default: return '';
    }
  };

  const handleStatusChange = async (bookingId, newStatus) => {
    try {
      const response = await bookingsAPI.update(bookingId, { status: newStatus });
      setBookings(prev => prev.map(booking => 
        booking.id === bookingId ? (response.data?.data || response.data) : booking
      ));
    } catch (error) {
      console.error('Error updating booking status:', error);
    }
  };

  // Function to format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  // Function to format datetime for display
  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return '';
    const date = new Date(dateTimeString);
    return date.toLocaleString();
  };

  // Function to open booking details modal
  const openBookingDetails = (booking) => {
    setSelectedBooking(booking);
    setShowBookingDetails(true);
  };

  // Function to close booking details modal
  const closeBookingDetails = () => {
    setShowBookingDetails(false);
    setSelectedBooking(null);
  };

  // Function to handle sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Function to get sort indicator
  const getSortIndicator = (columnName) => {
    if (sortConfig.key === columnName) {
      return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
    }
    return '';
  };

  // Export function
  const exportBookingsToExcel = () => {
    // Prepare data for export
    const exportData = filteredBookings.map(booking => ({
      'Booking ID': booking.id || '',
      'Customer Name': booking.customer_name || '',
      'Customer Email': booking.customer_email || '',
      'Customer Phone': booking.guest_info?.phone || '',
      'Hotel Name': booking.hotel_name || '',
      'Check In': booking.check_in_date || '',
      'Check Out': booking.check_out_date || '',
      Guests: booking.number_of_guests || '',
      'Total Amount': `${booking.currency || 'USD'} ${booking.total_price || ''}`,
      Status: booking.status ? booking.status.charAt(0).toUpperCase() + booking.status.slice(1) : '',
      'Special Requests': booking.special_requests || '',
      'Booking Date': booking.booking_date || ''
    }));
    
    exportToExcel(exportData, 'bookings', 'Bookings');
  };

  return (
    <div className="bookings-page">
      <div className="page-header">
        <h1>Bookings Management</h1>
        <button className="btn btn-primary" onClick={exportBookingsToExcel}>
          Export to Excel
        </button>
      </div>

      <div className="bookings-controls">
        <div className="filter-row">
          <div className="filter-group">
            <label htmlFor="hotel-filter">Hotel</label>
            <select 
              id="hotel-filter"
              value={hotelFilter}
              onChange={(e) => setHotelFilter(e.target.value)}
            >
              <option value="">All Hotels</option>
              {hotels.map(hotel => (
                <option key={hotel.id} value={hotel.name_en || hotel.name || hotel.hotel_name || hotel.id}>
                  {hotel.name_en || hotel.name || hotel.hotel_name || hotel.id}
                </option>
              ))}
            </select>
          </div>
          
          <div className="filter-group">
            <label htmlFor="check-in-date">Check-in Date</label>
            <input
              type="date"
              id="check-in-date"
              value={checkInDateFilter}
              onChange={(e) => setCheckInDateFilter(e.target.value)}
            />
          </div>
          
          <div className="filter-group">
            <label htmlFor="check-out-date">Check-out Date</label>
            <input
              type="date"
              id="check-out-date"
              value={checkOutDateFilter}
              onChange={(e) => setCheckOutDateFilter(e.target.value)}
            />
          </div>
        </div>
        
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by booking ID, customer, or hotel..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <span className="search-icon"><FaSearch /></span>
        </div>
        
        <div className="filter-box">
          <select value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="all">All Bookings</option>
            <option value="confirmed">Confirmed</option>
            <option value="pending">Pending</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      <div className="bookings-list">
        <h2>Bookings List</h2>
        <table className="bookings-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('id')}>Booking ID{getSortIndicator('id')}</th>
              <th onClick={() => handleSort('customer_name')}>Customer{getSortIndicator('customer_name')}</th>
              <th onClick={() => handleSort('hotel_name')}>Hotel{getSortIndicator('hotel_name')}</th>
              <th onClick={() => handleSort('check_in_date')}>Dates{getSortIndicator('check_in_date')}</th>
              <th onClick={() => handleSort('number_of_guests')}>Guests{getSortIndicator('number_of_guests')}</th>
              <th onClick={() => handleSort('total_price')}>Amount{getSortIndicator('total_price')}</th>
              <th onClick={() => handleSort('status')}>Status{getSortIndicator('status')}</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedBookings.map(booking => (
              <tr key={booking.id}>
                <td>{booking.id || ''}</td>
                <td>
                  <div className="customer-info">
                    <div className="customer-name">{booking.customer_name || ''}</div>
                    <div className="customer-email">{booking.customer_email || ''}</div>
                    <div className="customer-phone">{booking.guest_info?.phone || booking.user?.phone || ''}</div>
                  </div>
                </td>
                <td>
                  <div className="hotel-info">
                    <div className="hotel-name">{booking.hotel_name || ''}</div>
                  </div>
                </td>
                <td>
                  <div className="date-info">
                    <div>{booking.check_in_date || ''} to</div>
                    <div>{booking.check_out_date || ''}</div>
                  </div>
                </td>
                <td>{booking.number_of_guests || ''}</td>
                <td>{booking.currency || 'USD'} {booking.total_price || ''}</td>
                <td>
                  <span className={`status-badge ${getStatusClass(booking.status)}`}>
                    {booking.status ? booking.status.charAt(0).toUpperCase() + booking.status.slice(1) : ''}
                  </span>
                </td>
                <td>
                  <div className="action-buttons">
                    <button 
                      className="btn btn-small btn-edit"
                      onClick={() => handleStatusChange(booking.id, 'confirmed')}
                      disabled={booking.status === 'confirmed'}
                    >
                      Confirm
                    </button>
                    <button 
                      className="btn btn-small btn-delete"
                      onClick={() => handleStatusChange(booking.id, 'cancelled')}
                      disabled={booking.status === 'cancelled'}
                    >
                      Cancel
                    </button>
                    <button 
                      className="btn btn-small btn-view"
                      onClick={() => openBookingDetails(booking)}
                    >
                      View More
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {sortedBookings.length === 0 && (
          <div className="no-bookings">
            <p>No bookings found matching your criteria.</p>
          </div>
        )}
      </div>

      {/* Booking Details Modal */}
      {showBookingDetails && selectedBooking && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Booking Details - #{selectedBooking.id}</h3>
              <button className="modal-close-btn" onClick={closeBookingDetails}>×</button>
            </div>
            <div className="modal-body">
              <div className="booking-details-grid">
                <div className="detail-item">
                  <label>Booking ID:</label>
                  <span>{selectedBooking.id}</span>
                </div>
                <div className="detail-item">
                  <label>Customer Name:</label>
                  <span>{selectedBooking.customer_name}</span>
                </div>
                <div className="detail-item">
                  <label>Customer Email:</label>
                  <span>{selectedBooking.customer_email}</span>
                </div>
                <div className="detail-item">
                  <label>Customer Phone:</label>
                  <span>{selectedBooking.guest_info?.phone || selectedBooking.user?.phone || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>Hotel Name:</label>
                  <span>{selectedBooking.hotel_name}</span>
                </div>
                <div className="detail-item">
                  <label>Room Name:</label>
                  <span>{selectedBooking.room_name}</span>
                </div>
                <div className="detail-item">
                  <label>Check-in Date:</label>
                  <span>{formatDate(selectedBooking.check_in_date)}</span>
                </div>
                <div className="detail-item">
                  <label>Check-out Date:</label>
                  <span>{formatDate(selectedBooking.check_out_date)}</span>
                </div>
                <div className="detail-item">
                  <label>Number of Guests:</label>
                  <span>{selectedBooking.number_of_guests}</span>
                </div>
                <div className="detail-item">
                  <label>Total Price:</label>
                  <span>{selectedBooking.currency} {selectedBooking.total_price}</span>
                </div>
                <div className="detail-item">
                  <label>Currency:</label>
                  <span>{selectedBooking.currency}</span>
                </div>
                <div className="detail-item">
                  <label>Status:</label>
                  <span className={`status-badge ${getStatusClass(selectedBooking.status)}`}>
                    {selectedBooking.status ? selectedBooking.status.charAt(0).toUpperCase() + selectedBooking.status.slice(1) : ''}
                  </span>
                </div>
                <div className="detail-item">
                  <label>Payment Status:</label>
                  <span>{selectedBooking.payment_status}</span>
                </div>
                <div className="detail-item">
                  <label>Special Requests:</label>
                  <span>{selectedBooking.special_requests || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>External Hotel ID:</label>
                  <span>{selectedBooking.external_hotel_id}</span>
                </div>
                <div className="detail-item">
                  <label>Booking Reference:</label>
                  <span>{selectedBooking.booking_reference}</span>
                </div>
                <div className="detail-item">
                  <label>Created At:</label>
                  <span>{formatDateTime(selectedBooking.created_at)}</span>
                </div>
                <div className="detail-item">
                  <label>Updated At:</label>
                  <span>{formatDateTime(selectedBooking.updated_at)}</span>
                </div>
                <div className="detail-item full-width">
                  <label>User Info:</label>
                  <div className="nested-details">
                    <div><strong>ID:</strong> {selectedBooking.user?.id}</div>
                    <div><strong>Name:</strong> {selectedBooking.user?.name}</div>
                    <div><strong>Email:</strong> {selectedBooking.user?.email}</div>
                    <div><strong>Phone:</strong> {selectedBooking.user?.phone}</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={closeBookingDetails}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Bookings;